import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getTriageSession, stopTriageSession, replyToSession, enableAutoMerge } from '../api/triage';
import { getFinding } from '../api/findings';
import { formatDate } from '../lib/format';

const POLL_INTERVAL = 5_000;

interface PrsPanelProps {
  prsOpened: Array<{ prNumber: number; title: string; ciStatus: string; mergeState: string }>;
}

export function PrsPanel({ prsOpened }: PrsPanelProps): React.ReactElement {
  return (
    <div className="bg-white border border-border rounded-lg p-4">
      <h2 className="text-sm font-semibold text-text-secondary mb-3">Pull Requests</h2>
      <ul className="space-y-2">
        {prsOpened.map((pr) => (
          <li key={pr.prNumber} className="flex items-center justify-between text-sm">
            <div>
              <span className="font-medium">#{pr.prNumber}</span>
              <span className="ml-2 text-text-secondary">{pr.title}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-2 py-0.5 rounded-full text-xs ${pr.ciStatus === 'green' ? 'bg-green-100 text-green-800' : pr.ciStatus === 'red' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700'}`}>
                CI: {pr.ciStatus}
              </span>
              <span className="text-text-muted">{pr.mergeState}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface ActivityPanelProps {
  sessionId: string;
  status: string;
  pauseReason?: string;
  reply: string;
  replyPending: boolean;
  onReplyChange: (v: string) => void;
  onReplySubmit: () => void;
}

export function ActivityPanel({
  status, pauseReason, reply, replyPending, onReplyChange, onReplySubmit,
}: ActivityPanelProps): React.ReactElement {
  return (
    <div className="bg-white border border-border rounded-lg p-4 flex flex-col">
      <h2 className="text-sm font-semibold text-text-secondary mb-3">Activity Stream</h2>
      <div className="flex-1 space-y-2 text-sm text-text-secondary overflow-y-auto max-h-64">
        <p className="text-text-muted italic">
          Session {status === 'in_progress' ? 'active — polling every 5s' : 'ended'}.
        </p>
        {pauseReason && <p className="text-yellow-700">Paused: {pauseReason}</p>}
      </div>
      {status === 'in_progress' && (
        <form
          onSubmit={(e) => { e.preventDefault(); if (reply.trim()) onReplySubmit(); }}
          className="mt-3 flex gap-2"
        >
          <input
            value={reply}
            onChange={(e) => onReplyChange(e.target.value)}
            placeholder="Send a message…"
            className="flex-1 text-sm border border-border rounded-md px-3 py-1.5"
          />
          <button
            type="submit"
            disabled={!reply.trim() || replyPending}
            className="px-3 py-1.5 text-sm bg-brand-600 text-white rounded-md disabled:opacity-50"
          >
            Send
          </button>
        </form>
      )}
    </div>
  );
}

function FindingPreview({ findingId }: { findingId: string }): React.ReactElement {
  const { data } = useQuery({
    queryKey: ['finding-preview', findingId],
    queryFn: async () => {
      const statuses = ['open', 'triaging', 'in_progress', 'pr_open'] as const;
      for (const s of statuses) {
        try { return await getFinding(s, findingId); } catch { /* try next */ }
      }
      return null;
    },
    staleTime: 60_000,
  });

  return (
    <li className="text-sm text-text-secondary">
      {data ? (
        <span>{data.summary} <span className="text-text-muted">({data.category})</span></span>
      ) : (
        <span className="font-mono text-xs text-text-muted">{findingId.slice(0, 12)}…</span>
      )}
    </li>
  );
}

type SessionData = Awaited<ReturnType<typeof getTriageSession>>;

interface SessionHeaderProps {
  session: SessionData;
  autoMergePending: boolean;
  stopPending: boolean;
  onAutoMerge: () => void;
  onStop: () => void;
}

export function SessionHeader({
  session, autoMergePending, stopPending, onAutoMerge, onStop,
}: SessionHeaderProps): React.ReactElement {
  return (
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-xl font-semibold text-text-primary">Triage Session</h1>
        <p className="text-sm text-text-muted">{session.sessionId.slice(0, 16)}… · {session.actorEmail}</p>
      </div>
      <div className="flex items-center gap-3">
        {!session.allowAutoMerge && session.status === 'in_progress' && (
          <button onClick={onAutoMerge} disabled={autoMergePending} className="px-3 py-1.5 text-sm border border-brand-600 text-brand-600 rounded-md hover:bg-brand-50">
            Enable Auto-Merge
          </button>
        )}
        {session.status === 'in_progress' && (
          <button onClick={onStop} disabled={stopPending} className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700">
            Stop Session
          </button>
        )}
      </div>
    </div>
  );
}

interface FindingsPanelProps {
  findingIds: string[];
  session: SessionData;
  reply: string;
  replyPending: boolean;
  onReplyChange: (v: string) => void;
  onReplySubmit: () => void;
}

export function FindingsPanel({ findingIds, session, reply, replyPending, onReplyChange, onReplySubmit }: FindingsPanelProps): React.ReactElement {
  return (
    <div className="grid grid-cols-2 gap-6">
      <div className="bg-white border border-border rounded-lg p-4">
        <h2 className="text-sm font-semibold text-text-secondary mb-3">Findings</h2>
        <ul className="space-y-2">
          {findingIds.map((fid) => <FindingPreview key={fid} findingId={fid} />)}
        </ul>
      </div>
      <ActivityPanel
        sessionId={session.sessionId}
        status={session.status}
        pauseReason={session.pauseReason}
        reply={reply}
        replyPending={replyPending}
        onReplyChange={onReplyChange}
        onReplySubmit={onReplySubmit}
      />
    </div>
  );
}

export function TriageSessionDetail(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [reply, setReply] = useState('');
  const [stopReason, setStopReason] = useState('');

  const { data: session, isLoading } = useQuery({
    queryKey: ['triage-session', id],
    queryFn: () => getTriageSession(id!),
    enabled: !!id,
    refetchInterval: (query) =>
      query.state.data?.status === 'in_progress' ? POLL_INTERVAL : false,
  });

  const stopMut = useMutation({
    mutationFn: (reason?: string) => stopTriageSession(id!, reason),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['triage-session', id] }),
  });

  const replyMut = useMutation({
    mutationFn: (msg: string) => replyToSession(id!, msg),
    onSuccess: () => { setReply(''); void queryClient.invalidateQueries({ queryKey: ['triage-session', id] }); },
  });

  const autoMergeMut = useMutation({
    mutationFn: () => enableAutoMerge(id!),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['triage-session', id] }),
  });

  if (isLoading) return <p className="text-sm text-text-muted">Loading…</p>;
  if (!session) return <p className="text-sm text-red-600">Session not found.</p>;

  const statusCls = session.status === 'in_progress' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700';

  return (
    <div className="space-y-4">
      <SessionHeader session={session} autoMergePending={autoMergeMut.isPending} stopPending={stopMut.isPending} onAutoMerge={() => autoMergeMut.mutate()} onStop={() => stopMut.mutate(stopReason || undefined)} />
      <div className="flex gap-2">
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusCls}`}>{session.status}</span>
        {session.allowAutoMerge && <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">Auto-merge ON</span>}
        <span className="text-xs text-text-muted">Started {formatDate(session.startedAt)}</span>
      </div>
      <FindingsPanel findingIds={session.findingsExamined} session={session} reply={reply} replyPending={replyMut.isPending} onReplyChange={setReply} onReplySubmit={() => replyMut.mutate(reply)} />
      {session.prsOpened.length > 0 && <PrsPanel prsOpened={session.prsOpened} />}
      {session.status === 'in_progress' && (
        <div className="flex gap-2">
          <input value={stopReason} onChange={(e) => setStopReason(e.target.value)} placeholder="Stop reason (optional)" className="text-sm border border-border rounded-md px-3 py-1.5 w-64" />
        </div>
      )}
    </div>
  );
}
